/**
 * Чистая логика мастера «Пересоздать карточки» по выбранным товарам или макетам.
 *
 * Мастер в карточке макета строится на матрице одного макета. Здесь выбор
 * начинается с товаров, а макетов в выборке много — поэтому появляются три
 * задачи, которых там нет: свести планы нескольких макетов в один список
 * кабинетов, предвыбрать категории по тем товарам, что человек отметил, и
 * собрать общий план воронок из планов каждого макета.
 *
 * Ключ выбора воронки (`template_key:категория:площадка:кабинет`) кода макета
 * не содержит — значит у макетов одного типа строки плана совпадают, и один
 * выбор человека применяется ко всем. На этом и держится общий третий шаг.
 */

import type { FunnelPlanRow } from '../contentFunnelSelection.types'
import { funnelPlanRowKey } from '../contentFunnelSelection.logic'
import type {
  RegenerationBatchItem,
  RegenerationPlan,
  RegenerationPlanCabinet,
} from '../../features/pipeline/api'

export const cabinetKey = (cabinet: { marketplace: string; seller_id: number }): string =>
  `${cabinet.marketplace}:${cabinet.seller_id}`

export interface DesignPlanEntry {
  code: string
  plan: RegenerationPlan
}

/**
 * Кабинеты всех макетов выборки одним списком.
 *
 * Кабинет считается «был в прогоне», если участвовал хотя бы у одного макета:
 * предвыбор должен повторять поведение кнопки без мастера, а она берёт кабинеты
 * прошлых прогонов каждого макета отдельно.
 */
export function mergeCabinets(entries: DesignPlanEntry[]): RegenerationPlanCabinet[] {
  const merged = new Map<string, RegenerationPlanCabinet>()
  for (const entry of entries) {
    for (const cabinet of entry.plan.cabinets || []) {
      const key = cabinetKey(cabinet)
      const previous = merged.get(key)
      if (!previous) {
        merged.set(key, { ...cabinet })
        continue
      }
      if (cabinet.used_before) merged.set(key, { ...previous, used_before: true })
    }
  }
  return [...merged.values()]
}

/**
 * Предвыбор категорий: те, где лежат отмеченные человеком товары.
 *
 * Если ни один артикул макета в выборку не попал — а так бывает, когда товар
 * матрицей положен, но ещё не создан, — берём все категории макета. Пустой
 * предвыбор означал бы «макет выбран, но делать нечего», и человек бы этого не
 * понял.
 */
export function initialCategories(
  entries: DesignPlanEntry[],
  selectedArticles: string[],
): Record<string, number[]> {
  const articles = new Set(selectedArticles.map((article) => String(article || '').trim()))
  const result: Record<string, number[]> = {}
  for (const entry of entries) {
    const categories = entry.plan.categories || []
    const matched = categories.filter((category) =>
      (category.articles || []).some((article) => articles.has(String(article))),
    )
    result[entry.code] = (matched.length ? matched : categories).map(
      (category) => category.category_id,
    )
  }
  return result
}

export function toggleCategory(
  selected: Record<string, number[]>,
  code: string,
  categoryId: number,
): Record<string, number[]> {
  const current = selected[code] || []
  return {
    ...selected,
    [code]: current.includes(categoryId)
      ? current.filter((value) => value !== categoryId)
      : [...current, categoryId],
  }
}

/** Макеты, которым есть что перегенерировать. */
export function activeDesigns(
  entries: DesignPlanEntry[],
  selected: Record<string, number[]>,
): DesignPlanEntry[] {
  return entries.filter((entry) => (selected[entry.code] || []).length > 0)
}

/** Сколько товаров уйдёт в прогон и сколько из них будет создано впервые. */
export function selectionSummary(
  entries: DesignPlanEntry[],
  selected: Record<string, number[]>,
): { designs: number; products: number; created: number } {
  let products = 0
  let created = 0
  let designs = 0
  for (const entry of entries) {
    const ids = selected[entry.code] || []
    if (!ids.length) continue
    designs += 1
    for (const category of entry.plan.categories || []) {
      if (!ids.includes(category.category_id)) continue
      products += Math.max(1, category.quantity)
      created += Math.max(0, category.missing)
    }
  }
  return { designs, products, created }
}

export function selectedTargets(
  cabinets: RegenerationPlanCabinet[],
  keys: string[],
): Array<{ marketplace: string; seller_id: number; cabinet: string }> {
  return cabinets
    .filter((cabinet) => keys.includes(cabinetKey(cabinet)))
    .map((cabinet) => ({
      marketplace: cabinet.marketplace,
      seller_id: cabinet.seller_id,
      cabinet: cabinet.cabinet,
    }))
}

/**
 * Общий план воронок: строки планов всех макетов, схлопнутые по ключу выбора.
 *
 * Дубли неизбежны и полезны — у макетов одного типа строки совпадают, и человек
 * должен отвечать на вопрос один раз, а не двадцать. Порядок первого появления
 * сохраняем: он задаёт порядок вопросов на экране.
 */
export function mergeFunnelRows(rowsByDesign: Record<string, FunnelPlanRow[]>): FunnelPlanRow[] {
  const merged = new Map<string, FunnelPlanRow>()
  for (const rows of Object.values(rowsByDesign)) {
    for (const row of rows || []) {
      const key = funnelPlanRowKey(row)
      if (!merged.has(key)) merged.set(key, row)
    }
  }
  return [...merged.values()]
}

/**
 * Сколько макетов мастер отправляет одним пакетным запросом. Бэкенд принимает
 * до 100; 50 держит запрос в паре секунд и даёт видимый прогресс «N из M».
 */
export const PLAN_BATCH_SIZE = 50

export function chunked<T>(values: T[], size: number): T[][] {
  const chunks: T[][] = []
  for (let index = 0; index < values.length; index += size) {
    chunks.push(values.slice(index, index + size))
  }
  return chunks
}

/**
 * Результаты пачки в порядке ответа. Шаг мастера по-прежнему падает целиком,
 * если не посчитался хоть один макет: раньше так вёл себя поштучный запрос, и
 * человек должен видеть, какой именно макет мешает, а не получить выборку
 * с молча выпавшими товарами.
 */
export function unpackBatch<T>(
  items: Array<RegenerationBatchItem<T>>,
): Array<{ code: string; result: T }> {
  return items.map((item) => {
    if (item.error || item.result === undefined) {
      throw new Error(
        `Макет #${item.design_code}: ${item.error?.message || 'план не построен'}`,
      )
    }
    return { code: item.design_code, result: item.result }
  })
}
